// src/pages/campaigns/forms/CampaignBudgetForm.jsx
import React, { useState, useEffect } from 'react';
import { getDynamicCurrencyOptions } from '../../../utils/dynamicCurrencyUtils';
import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Box,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  PieChart as AllocationIcon,
  Receipt as TransactionIcon,
  Info as InfoIcon,
  Percent as PercentIcon,
  Timeline as BudgetingIcon,
} from '@mui/icons-material';

// Import FormSection component
import { FormSection } from '../../../design-system/components/forms';

// Dynamic currency options will be loaded from API

// Platform list for budget allocation
const PLATFORMS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'twitter', label: 'Twitter' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'snapchat', label: 'Snapchat' },
  { key: 'googleAds', label: 'Google Ads' },
  { key: 'other', label: 'Other' },
];

// Campaign budget form component
const CampaignBudgetForm = ({
  campaignData,
  onChange,
  onNestedChange,
  errors = {},
  touched = {}
}) => {
  const theme = useTheme();
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Load currencies dynamically
  useEffect(() => {
    const loadCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const currencyOptions = await getDynamicCurrencyOptions();
        setCurrencies(currencyOptions);
      } catch (error) {
        console.error('Failed to load currencies:', error);
        setCurrencies([]);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    
    loadCurrencies();
  }, []);
  
  // Get budget data or initialize
  const budget = campaignData.budget || {
    total: 0,
    currency: 'USD',
    allocation: {},
    spent: 0,
    remaining: 0,
    transactions: []
  };

  const [allocations, setAllocations] = useState({});
  const [totalAllocation, setTotalAllocation] = useState(0);
  
  // Initialize allocations from campaign data
  useEffect(() => {
    // Convert allocation from budget to state for sliders
    const initialAllocations = {};
    PLATFORMS.forEach(platform => {
      const allocation = budget.allocation?.[platform.key];
      initialAllocations[platform.key] = allocation ? 
        (allocation.percentage || 0) : 
        0;
    });
    
    setAllocations(initialAllocations);
    setTotalAllocation(
      Object.values(initialAllocations).reduce((sum, val) => sum + val, 0)
    );
  }, [budget.allocation]);

  // Handle total budget change
  const handleTotalBudgetChange = (e) => {
    // Only accept positive integers
    let value = parseInt(e.target.value, 10) || 0;
    
    // Ensure value is positive
    if (value < 0) {
      value = 0;
    }
    
    // Round to nearest integer to ensure we're using whole numbers
    value = Math.round(value);
    
    onNestedChange('budget', 'total', value);
    
    // Update allocation amounts based on percentages
    const updatedAllocation = {};
    
    Object.entries(allocations).forEach(([platform, percentage]) => {
      updatedAllocation[platform] = {
        percentage,
        amount: (percentage / 100) * value
      };
    });
    
    onNestedChange('budget', 'allocation', updatedAllocation);
    onNestedChange('budget', 'remaining', value - (budget.spent || 0));
  };

  // Handle currency change
  const handleCurrencyChange = (e) => {
    onNestedChange('budget', 'currency', e.target.value);
  };

  // Handle allocation slider change
  const handleAllocationChange = (platform, newValue) => {
    // Calculate new total allocation
    const oldValue = allocations[platform] || 0;
    const newTotal = totalAllocation - oldValue + newValue;
    
    // Don't allow total allocation to exceed 100%
    if (newTotal > 100) {
      return;
    }
    
    // Update allocations state
    const newAllocations = {
      ...allocations,
      [platform]: newValue
    };
    
    setAllocations(newAllocations);
    setTotalAllocation(newTotal);
    
    // Update budget allocation in campaign data
    const totalBudget = budget.total || 0;
    const updatedAllocation = {};
    
    Object.entries(newAllocations).forEach(([key, percentage]) => {
      updatedAllocation[key] = {
        percentage,
        amount: (percentage / 100) * totalBudget
      };
    });
    
    onNestedChange('budget', 'allocation', updatedAllocation);
  };

  // Get currency symbol
  const getCurrencySymbol = () => {
    const currency = currencies.find(c => c.value === budget.currency);
    return currency ? currency.symbol : '$';
  };

  // Function to render the main budget fields
  const renderBudgetFields = () => (
    <Grid container spacing={3}>
      {/* Total Budget */}
      <Grid item xs={12} md={6}>
        <TextField
          label="Total Budget *"
          type="number"
          value={budget.total || ''}
          onChange={handleTotalBudgetChange}
          fullWidth
          required
          inputProps={{ 
            min: 0, // Only allow positive numbers
            step: "1" // Increment by whole numbers
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MoneyIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {getCurrencySymbol()}
              </InputAdornment>
            ),
          }}
          error={touched?.['budget.total'] && Boolean(errors?.['budget.total'])}
          helperText={touched?.['budget.total'] && errors?.['budget.total'] ? 
            errors?.['budget.total'] : 
            "The total budget amount for this campaign"}
          InputLabelProps={{
            error: touched?.['budget.total'] && Boolean(errors?.['budget.total']),
            required: true,
          }}
        />
      </Grid>

      {/* Currency */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel 
            id="currency-label"
            error={touched?.['budget.currency'] && Boolean(errors?.['budget.currency'])}
          >
            Currency
          </InputLabel>
          <Select
            labelId="currency-label"
            id="currency"
            value={budget.currency || 'USD'}
            onChange={handleCurrencyChange}
            label="Currency"
            error={touched?.['budget.currency'] && Boolean(errors?.['budget.currency'])}
          >
            {currencies.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  // Function to render platform allocations
  const renderBudgetAllocation = () => (
    <Grid container spacing={2}>
      {PLATFORMS.map((platform) => {
        // Skip platforms that aren't selected in the campaign
        if (platform.key !== 'other' && campaignData.platforms && 
            campaignData.platforms[platform.key] === false) {
          return null;
        }
        
        const allocation = allocations[platform.key] || 0;
        const budgetAmount = budget.total ? (allocation / 100) * budget.total : 0;
        
        return (
          <Grid item xs={12} key={platform.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {platform.label}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                {allocation}% ({getCurrencySymbol()}{budgetAmount.toFixed(2)})
              </Typography>
            </Box>
            <Slider
              value={allocation}
              onChange={(e, value) => handleAllocationChange(platform.key, value)}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              step={1}
              valueLabelFormat={(value) => `${value}%`}
            />
          </Grid>
        );
      })}

      {/* Total Budget Summary */}
      <Grid item xs={12}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 3,
          p: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 1
        }}>
          <Typography variant="subtitle2">
            Total Budget:
          </Typography>
          <Typography variant="subtitle2">
            {getCurrencySymbol()}{budget.total || 0}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 1,
          p: 2,
          bgcolor: totalAllocation === 100 
            ? alpha(theme.palette.success.main, 0.1)
            : alpha(theme.palette.warning.main, 0.1),
          borderRadius: 1
        }}>
          <Typography variant="subtitle2">
            Total Allocated:
          </Typography>
          <Typography variant="subtitle2">
            {totalAllocation}% ({getCurrencySymbol()}{(budget.total * totalAllocation / 100).toFixed(2)})
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  // Function to render budget planning tips
  const renderBudgetTips = () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: alpha(theme.palette.info.main, 0.1), 
      borderRadius: 1
    }}>
      <Typography variant="subtitle2" gutterBottom>
        Tips for Budget Planning:
      </Typography>
      <Typography variant="body2">
        • Prioritize platforms where your target audience is most active
      </Typography>
      <Typography variant="body2">
        • Consider the cost differences between platforms (e.g., LinkedIn typically costs more than Facebook)
      </Typography>
      <Typography variant="body2">
        • Reserve 10-15% as contingency for unexpected opportunities
      </Typography>
      <Typography variant="body2">
        • For testing new platforms, start with a smaller allocation (5-10%)
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Section 1: Campaign Budget */}
      <FormSection
        title="Campaign Budget"
        description="Define the total budget and currency for your marketing campaign."
        icon={<MoneyIcon />}
        tooltip="Set the overall budget that will be allocated across different platforms and activities."
      >
        {renderBudgetFields()}
      </FormSection>

      {/* Section 2: Budget Allocation */}
      <FormSection
        title="Platform Budget Allocation"
        description={`Distribute your budget across platforms (${totalAllocation}% allocated)`}
        icon={<AllocationIcon />}
        tooltip="Allocate percentages of your total budget to each platform you'll be using in this campaign."
      >
        {renderBudgetAllocation()}
      </FormSection>

      {/* Section 3: Budget Planning Tips */}
      <FormSection
        title="Budget Planning Best Practices"
        description="Recommendations for more effective budget allocation."
        icon={<BudgetingIcon />}
        tooltip="Tips to help you make more informed decisions about your campaign budget."
      >
        {renderBudgetTips()}
      </FormSection>
    </>
  );
};

export default CampaignBudgetForm;